import { match, P } from "ts-pattern";
import { AlarmScheduleV2, mode, type Mode } from "./alarm-schedule";
import type { Author } from "../schema";
import {
  ScheduleV2Compat,
  evaluateAt,
  evaluateStateRanges,
  dateTimeRecordToLocalisedDateTime,
  dateTimeToDateTimeRecord,
  type DateTimeRecord,
  type FeatureEvaluation,
} from "@visioni/schedules";

const MAX_HORIZON_DAYS = 30;

export type AlarmConfiguration = {
  /** The schedule currently running on the edge device, or `null` if none has been applied. */
  applied: AlarmScheduleV2 | null;
  /** The schedule the user has saved in the cloud, or `null` if the user hasn't configured one. */
  configured: AlarmScheduleV2 | null;
};

export type { Mode };

export type ModeAction = "arm" | "disarm" | "resume";

/**
 * The observable mode including in-flight transitions.
 *
 * - `"armed"` / `"disarmed"` — applied schedule state is authoritative
 * - `"arming"` — a newer configured schedule would arm, but the applied schedule is still disarmed
 * - `"disarming"` — a newer configured schedule would disarm, but the applied schedule is still armed
 */
export type ModeStatus = "armed" | "disarmed" | "arming" | "disarming";

/**
 * Describes the synchronisation relationship between configured and applied schedules.
 * Each variant carries the schedule values available in that state.
 *
 * - `"unconfigured"` — neither schedule exists
 * - `"inherited"` — applied exists but user hasn't configured one
 * - `"applying_initial"` — configured exists but nothing applied yet
 * - `"applying_change"` — configured is newer than applied, awaiting application
 * - `"applied"` — configured and applied share the same `updated_at`
 * - `"altered"` — applied is newer than configured (edge reinterpreted the timezone or there was an out-of-band change)
 */
export type SyncState =
  | { status: "unconfigured" }
  | { status: "inherited"; applied: AlarmScheduleV2 }
  | { status: "applying_initial"; configured: AlarmScheduleV2 }
  | { status: "applying_change"; applied: AlarmScheduleV2; configured: AlarmScheduleV2 }
  | { status: "applied"; applied: AlarmScheduleV2; configured: AlarmScheduleV2 }
  | { status: "altered"; applied: AlarmScheduleV2; configured: AlarmScheduleV2 };

/**
 * Derives the {@link SyncState} from nullability and `updated_at` timestamps.
 *
 * @remarks Plain narrowing instead of `match([applied, configured])` coz
 * ts-pattern hits TS2589 (excessively deep) on the cartesian product of the
 * tuple :(
 */
export const currSyncState = ({ applied, configured }: AlarmConfiguration): SyncState => {
  if (applied === null)
    return configured === null ? { status: "unconfigured" } : { status: "applying_initial", configured };
  if (configured === null) return { status: "inherited", applied };
  if (applied.metadata.updated_at > configured.metadata.updated_at) return { status: "altered", applied, configured };
  if (applied.metadata.updated_at < configured.metadata.updated_at)
    return { status: "applying_change", applied, configured };
  return { status: "applied", applied, configured };
};

/** Returns the current {@link Mode} of the applied schedule at {@link at}, or `null` if nothing is applied. */
export const currMode = (configuration: AlarmConfiguration, at: DateTimeRecord): Mode | null =>
  configuration.applied !== null ? mode(configuration.applied, at) : null;

/**
 * Returns the {@link NextModeReason} of the *winning* rule at {@link at} in
 * the applied schedule — i.e. the same rule (`routine` / `exception:N` /
 * `override` / `default`) the schedule's own evaluation picked when it
 * resolved the current mode. Used by the UI when it needs to know "which
 * rule produced this state right now" without re-implementing rule
 * precedence (which would silently drift if the schedule's precedence
 * rules ever change).
 *
 * Returns `null` if no schedule is applied.
 */
export const currModeReason = (
  configuration: AlarmConfiguration,
  at: DateTimeRecord,
): NextModeReason<AlarmRuleMeta> | null =>
  configuration.applied !== null ? evaluateModeAt(configuration.applied, at).reason : null;

/**
 * Derives the mode status by comparing applied and configured schedule states at {@link at}.
 *
 * Returns `null` when no applied schedule exists. When only applied exists (inherited),
 * reports its mode directly. When both exist, a transition (`"arming"` / `"disarming"`)
 * is only reported while applying — otherwise applied is authoritative.
 */
export const currModeStatus = (configuration: AlarmConfiguration, at: DateTimeRecord): ModeStatus | null =>
  match(currSyncState(configuration))
    .with({ status: "unconfigured" }, () => null)
    .with({ status: "applying_initial" }, () => null)
    .with({ status: "inherited" }, ({ applied }) => mode(applied, at))
    .with({ status: "applying_change" }, ({ applied, configured }) =>
      match([mode(applied, at), mode(configured, at)])
        .with(["armed", "disarmed"], () => "disarming" as const)
        .with(["disarmed", "armed"], () => "arming" as const)
        .otherwise(([a]) => a),
    )
    .with({ status: "applied" }, ({ applied }) => mode(applied, at))
    .with({ status: "altered" }, ({ applied }) => mode(applied, at))
    .exhaustive();

export type NextModeReason<TMeta = Record<string, unknown>> =
  | { kind: "routine" }
  | { kind: "override"; meta: TMeta }
  | { kind: "exception"; meta: TMeta }
  | { kind: "default" };

type AlarmRuleMeta = { id: string; author: Author | null };

const toRuleMeta = (meta: Record<string, unknown> | undefined): AlarmRuleMeta => ({
  id: typeof meta?.["id"] === "string" ? meta["id"] : "",
  author: (meta?.["author"] as Author | null) ?? null,
});

export type NextMode = { mode: Mode; at: DateTimeRecord; reason: NextModeReason<AlarmRuleMeta> };

/**
 * Returns the next scheduled mode change after {@link at}, searching up to {@link horizon}.
 *
 * Evaluates all features' state ranges in a single pass and finds the earliest
 * point where the aggregate mode ("armed" if any feature is armed) flips.
 * Returns `null` when no applied schedule exists or the mode doesn't change within the horizon.
 */
export const nextMode = (
  configuration: AlarmConfiguration,
  at: DateTimeRecord,
  horizon: DateTimeRecord,
): NextMode | null => {
  if (configuration.applied === null) return null;

  const schedule = configuration.applied;
  const v1 = ScheduleV2Compat.toV1(schedule);
  const features = Object.keys(schedule.features);

  const ordf = (a: DateTimeRecord, b: DateTimeRecord) =>
    a.year - b.year || a.month - b.month || a.day - b.day || a.hour - b.hour || a.minute - b.minute;
  const keyf = (dt: DateTimeRecord) =>
    dt.year * 100_000_000 + dt.month * 1_000_000 + dt.day * 10_000 + dt.hour * 100 + dt.minute;

  // Inline specialization of `evaluateModeAt()` that reuses the pre-computed
  // `v1` and `features`. The free function calls `ScheduleV2Compat.toV1()`
  // (which includes a full Zod parse) on every invocation; in this loop that
  // would redundantly re-parse the schedule for each candidate datetime.
  const modeAt = (dt: DateTimeRecord): { mode: Mode; reason: NextModeReason<AlarmRuleMeta> } =>
    evaluateModeAtWithVNV(schedule, v1, features, dt);

  const tz = schedule.metadata.timezone;
  const maxHorizon = dateTimeToDateTimeRecord(
    dateTimeRecordToLocalisedDateTime(at, tz).plus({ days: MAX_HORIZON_DAYS }),
  );
  const end = ordf(horizon, maxHorizon) < 0 ? horizon : maxHorizon;
  const current = modeAt(at).mode;

  // Range starts are the only points where any feature's state changes, so they're
  // the only candidates for an aggregate mode flip. evaluateStateRanges yields
  // completed ranges. A long-lived range (like armed day 1 to 5) isn't yielded until
  // the state ends on day 5, so its start (day 1) arrives after shorter ranges.
  // We can't bail on first match so instead we track the earliest transition found
  // and skip the modeAt call for any candidate that's already past it.
  let best: NextMode | null = null;
  const seen = new Set<number>();
  for (const { start } of evaluateStateRanges(v1, features, { start: at, end })) {
    const key = keyf(start);
    if (seen.has(key)) continue;
    seen.add(key);
    if (best !== null && ordf(start, best.at) >= 0) continue;
    const { mode: m, reason } = modeAt(start);
    if (m !== current) best = { mode: m, at: start, reason };
  }

  return best;
};

/**
 * Free helper: which rule is winning the mode evaluation at {@link at}, and
 * what aggregate mode does it produce, for {@link schedule}? Mirrors the
 * inline `modeAt` used by {@link nextMode}, but accepts a schedule (rather
 * than the loop's pre-computed v1 + features) so callers that only need a
 * single point-in-time evaluation can use it without setting up the loop.
 *
 * Picks the *highest-priority* rule among the affecting features, matching
 * the schedule's own precedence order (`toV1` flattens V2 rule keys into
 * insertion order, so a higher index in `v1.rules` wins).
 */
const evaluateModeAt = (
  schedule: AlarmScheduleV2,
  at: DateTimeRecord,
): { mode: Mode; reason: NextModeReason<AlarmRuleMeta> } => {
  const v1 = ScheduleV2Compat.toV1(schedule);
  const features = Object.keys(schedule.features);
  return evaluateModeAtWithVNV(schedule, v1, features, at);
};

/**
 * Implementation of {@link evaluateModeAt} that takes the pre-computed `v1`
 * and `features` arrays so {@link nextMode}'s inner loop can reuse them
 * across many candidate datetimes without re-parsing the schedule each time.
 */
const evaluateModeAtWithVNV = (
  schedule: AlarmScheduleV2,
  v1: ReturnType<typeof ScheduleV2Compat.toV1>,
  features: readonly string[],
  at: DateTimeRecord,
): { mode: Mode; reason: NextModeReason<AlarmRuleMeta> } => {
  // Maps a V1 rule ID back to the V2 rule category that produced it.
  // toV1 flattens V2's keyed rule structure into a flat array using the
  // convention `"<key>"` for single rules and `"<key>:<index>"` for arrays.
  // Replace this with direct V2 rule-category lookup if a V2-native
  // evaluation engine is added.
  const priorities = new Map(v1.rules.map((r, i) => [r.id, i]));
  const priority = (best: FeatureEvaluation | undefined, e: FeatureEvaluation) =>
    match([best?.rule ?? null, e.rule] as const)
      .with([P._, null], () => best)
      .with([null, P.not(null)], () => e)
      .with(
        [P.not(null), P.not(null)],
        ([b, r]) => (priorities.get(r.id) ?? -1) > (priorities.get(b.id) ?? -1),
        () => e,
      )
      .otherwise(() => best);

  const evals = evaluateAt(v1, features, at);
  const modeValue: Mode = evals.some((f) => f.state === "armed") ? "armed" : "disarmed";
  const ruleId = evals.reduce(priority, undefined)?.rule?.id;
  const reason: NextModeReason<AlarmRuleMeta> = (() => {
    if (ruleId === undefined) return { kind: "default" as const };
    const [prefix, indexStr] = ruleId.split(":");
    // The V2 schedule's `rules` keys come from one of two sources:
    //   - V2-native (e.g. {@link makeAlarmSchedule}) emits keyed array slots
    //     `rules.exceptions[]` / `rules.override` / `rules.routine[]`. The
    //     V1 ids produced by `toV1` are then `"exceptions:<i>"` etc., and
    //     meta lookup uses the flat array index.
    //   - V1-round-tripped (legacy `computeScheduleForAlarmSettings` →
    //     `fromV1`) preserves the original V1 rule ids verbatim, e.g.
    //     `"exception:<id>"` (singular) and `"override"`. Meta lives at
    //     `rules[id]` directly because `fromV1` keys by id.
    // We accept both shapes so both code paths produce sensible reasons.
    return match(prefix)
      .with("routine", () => ({ kind: "routine" as const }))
      .with("override", () => ({ kind: "override" as const, meta: toRuleMeta(metaForOverride(schedule, ruleId)) }))
      .with("exception", () => ({
        // V1-round-tripped exceptions: the rule id is "exception:<originalId>",
        // and meta has no `id` field, so we derive it from the suffix. This
        // is exactly the id that lives in `settings.exceptions[i].id`, so
        // the UI's lookup-by-id finds the right entry.
        kind: "exception" as const,
        meta: { ...toRuleMeta(metaForException(schedule, ruleId)), id: indexStr ?? "" },
      }))
      .with("exceptions", () => ({
        kind: "exception" as const,
        meta: toRuleMeta(schedule.rules.exceptions[Number(indexStr)]?.meta),
      }))
      .otherwise(() => ({ kind: "default" as const }));
  })();
  return { mode: modeValue, reason };
};

/**
 * Resolve the V2 rule entry for an override across both schedule shapes:
 * V2-native (`rules.override`) and V1-round-tripped (the V1 id `"override"`
 * is preserved as a top-level key in V2 `rules`).
 */
const metaForOverride = (schedule: AlarmScheduleV2, ruleId: string): Record<string, unknown> | undefined => {
  if (schedule.rules.override !== undefined) return schedule.rules.override.meta;
  const generic = schedule.rules as unknown as Record<string, { meta?: Record<string, unknown> } | undefined>;
  return generic[ruleId]?.meta;
};

/**
 * Resolve the V2 rule entry for an exception across both schedule shapes.
 * V2-native uses `rules.exceptions[i]` (and the rule id is `"exceptions:<i>"`);
 * V1-round-tripped uses a top-level `rules["exception:<id>"]` entry.
 */
const metaForException = (schedule: AlarmScheduleV2, ruleId: string): Record<string, unknown> | undefined => {
  const generic = schedule.rules as unknown as Record<string, { meta?: Record<string, unknown> } | undefined>;
  return generic[ruleId]?.meta;
};

/**
 * Returns the available action given the current state, or `null` if no schedule is actionable.
 *
 * During an `applying_change` transition the *configured* schedule determines the action,
 * so the user can counteract the pending change (e.g. press "arm" while disarming).
 * Otherwise the applied schedule is authoritative.
 *
 * `"resume"` when an override rule is active, `"disarm"` when armed
 * by the schedule, `"arm"` when disarmed.
 */
export const currAction = (configuration: AlarmConfiguration, at: DateTimeRecord): ModeAction | null => {
  const schedule = match(currSyncState(configuration))
    .with({ status: "unconfigured" }, () => null)
    .with({ status: "applying_initial" }, () => null)
    .with({ status: "inherited" }, ({ applied }) => applied)
    .with({ status: "applying_change" }, ({ configured }) => configured)
    .with({ status: "applied" }, ({ applied }) => applied)
    .with({ status: "altered" }, ({ applied }) => applied)
    .exhaustive();

  if (schedule === null) return null;

  const v1 = ScheduleV2Compat.toV1(schedule);
  const features = Object.keys(schedule.features);
  const evals = evaluateAt(v1, features, at);

  // TOCONSIDER: Should we treat silence as an override?
  const hasOverride = evals.some(({ rule }) => rule !== null && rule.id === "override");
  const m: Mode = evals.some((f) => f.state === "armed") ? "armed" : "disarmed";

  return match([hasOverride, m] as const)
    .with([true, P._], () => "resume" as const)
    .with([false, "armed"], () => "disarm" as const)
    .with([false, "disarmed"], () => "arm" as const)
    .exhaustive();
};
